import { BaseAPI } from './BaseAPI';
import { Coins, AccAddress, Dec, ValAddress } from '../../../core';

export interface DelegatorRewards {
  rewards: {
    [validator: string]: Coins;
  };
  total: Coins;
}

export interface DistributionParams {
  community_tax: Dec;
  base_proposer_reward: Dec;
  bonus_proposer_reward: Dec;
  withdraw_addr_enabled: boolean;
}

export namespace DistributionParams {
  export interface Data {
    community_tax: string;
    base_proposer_reward: string;
    bonus_proposer_reward: string;
    withdraw_addr_enabled: boolean;
  }
}

export interface Rewards {
  rewards: {
    [validator: string]: Coins;
  };
  total: Coins;
}

export namespace Rewards {
  export interface Data {
    rewards: {
      validator_address: ValAddress;
      reward: Coins.Data;
    }[];
    total: Coins.Data;
  }
}

export interface ValidatorRewards {
  self_bond_rewards: Coins;
  val_commission: Coins;
}

export namespace ValidatorRewards {
  export interface Data {
    self_bond_rewards: Coins.Data;
    val_commission: Coins.Data;
  }
}

export class DistributionAPI extends BaseAPI {
  public async rewards(delegator: AccAddress): Promise<Rewards> {
    const rewardsData = await this.c
      .get<Rewards.Data>(`/distribution/delegators/${delegator}/rewards`)
      .then(d => d.result);

    let rewards: Rewards['rewards'] = {};
    for (const reward of rewardsData.rewards) {
      rewards[reward.validator_address] = Coins.fromData(reward.reward);
    }
    return {
      rewards,
      total: Coins.fromData(rewardsData.total),
    };
  }

  public async validatorRewards(
    validator: ValAddress
  ): Promise<ValidatorRewards> {
    return this.c
      .get<ValidatorRewards.Data>(`/distribution/validators/${validator}`)
      .then(d => d.result)
      .then(d => ({
        self_bond_rewards: Coins.fromData(d.self_bond_rewards),
        val_commission: Coins.fromData(d.val_commission),
      }));
  }

  public async withdrawAddress(delegator: AccAddress): Promise<AccAddress> {
    return this.c
      .get<AccAddress>(`/distribution/delegators/${delegator}/withdraw_address`)
      .then(d => d.result);
  }

  public async communityPool(): Promise<Coins> {
    return this.c
      .get<Coins.Data>(`/distribution/community_pool`)
      .then(d => Coins.fromData(d.result));
  }

  public async parameters(): Promise<DistributionParams> {
    return this.c
      .get<DistributionParams.Data>(`/distribution/parameters`)
      .then(d => d.result)
      .then(d => ({
        base_proposer_reward: new Dec(d.base_proposer_reward),
        community_tax: new Dec(d.community_tax),
        bonus_proposer_reward: new Dec(d.bonus_proposer_reward),
        withdraw_addr_enabled: d.withdraw_addr_enabled,
      }));
  }
}
